import { useRouter } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import type { EntryChanges } from '@realmery/realm-core';
import { RealmClientError, realms } from '../db/client.ts';
import { EntrySaver, type SaveStatus } from './entrySaver.ts';

/** Failures a retry cannot fix (realm.worker.ts#describeError). */
const FINAL_CODES = new Set(['not-found', 'entry-locked', 'invalid', 'too-new', 'damaged']);

/**
 * Autosaves one entry of one realm (docs/ENTRY-EDITOR-PLAN.md section 4.4) for as long as
 * the component using it is mounted. Everything waiting is written at once when the page
 * is hidden or closed, and when the entry is left. While anything is unsaved, closing
 * the page asks first.
 */
export function useEntrySaver(file: string, entryId: string): Autosaver<EntryChanges> {
  return useAutosaver<EntryChanges>((changes) => realms.updateEntry(file, entryId, changes));
}

export interface Autosaver<C extends object> {
  change: (changes: C) => void;
  /** Writes everything now; resolves once it is written or has failed. */
  flush: () => Promise<void>;
  status: SaveStatus;
  unsaved: () => boolean;
}

/** As useEntrySaver, for anything saved in parts the same way: a Story Studio scene. `write` is read once. */
export function useAutosaver<C extends object>(write: (changes: C) => Promise<void>): Autosaver<C> {
  const router = useRouter();
  const [status, setStatus] = useState<SaveStatus>({ state: 'saved' });
  /** Whether this entry was written while open: the router's copies of it are then out of date. */
  const [wrote] = useState(() => ({ any: false }));
  const [saver] = useState(
    () =>
      new EntrySaver<C>({
        write,
        onStatus: (next) => {
          if (next.state === 'saving') wrote.any = true;
          setStatus(next);
        },
        isFinal: (error) => error instanceof RealmClientError && error.code !== undefined && FINAL_CODES.has(error.code),
      }),
  );

  useEffect(() => {
    const flush = () => void saver.flush();
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') flush();
    };
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      if (!saver.unsaved) return;
      flush();
      event.preventDefault();
    };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', flush);
    window.addEventListener('beforeunload', onBeforeUnload);
    // Leaving for another page in the app: start writing at once.
    const stopListening = router.subscribe('onBeforeNavigate', flush);
    return () => {
      stopListening();
      // The router keeps loaded and preloaded pages for a while (preloaded ones for 30
      // seconds), and the next page may even have been read before the last change was
      // written. So once everything is written, every page reloads its data.
      const changed = () => wrote.any;
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', flush);
      window.removeEventListener('beforeunload', onBeforeUnload);
      // Leaving the entry: write what is left. The worker finishes it even after this page moves on.
      // A flush, not dispose(): React's strict mode runs this cleanup once and then the effect again.
      void saver.flush().then(() => {
        if (changed()) void router.invalidate();
      });
    };
  }, [saver, router]);

  return { change: (changes) => saver.change(changes), flush: () => saver.flush(), status, unsaved: () => saver.unsaved };
}

const LABELS: Record<SaveStatus['state'], string> = {
  saved: 'Saved',
  pending: 'Saving…',
  saving: 'Saving…',
  error: "Couldn't save",
};

/** "Saved", "Saving…" or "Couldn't save", read out by screen readers when it changes. */
export function SaveStatusLine({ status }: { status: SaveStatus }) {
  return (
    <p className={status.state === 'error' ? 'save-status save-status-error' : 'save-status'} role="status" aria-live="polite">
      {LABELS[status.state]}
      {status.state === 'error' && status.error ? `: ${status.error}` : ''}
    </p>
  );
}
